import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, X, CheckCircle, Sparkles, Car } from 'lucide-react';
import { API } from '../utils/apiConfig';
import { MatchCard } from '../components/MatchCard';

/**
 * Audio-Only Orb Voice-Intake.
 * No form fields — user speaks, Claude listens, extracts, confirms.
 * On complete=true → creates user via /api/auth/register-voice and shows the magic-link CTA.
 *
 * Browser STT + TTS. Works on Chromium-based browsers + iOS Safari.
 */
export default function OrbVoiceIntake() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'de').slice(0, 2);

  const [transcript, setTranscript] = useState('');
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [sessionId] = useState(() => `orb-intake-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [knownFields, setKnownFields] = useState({});
  const [lastReply, setLastReply] = useState('');
  const [complete, setComplete] = useState(false);
  const [registration, setRegistration] = useState(null); // {magic_link, user}
  const [demoMatch, setDemoMatch] = useState(null); // Demo MatchCard trigger
  const [error, setError] = useState('');
  const [thinking, setThinking] = useState(false);
  const recognitionRef = useRef(null);
  const hasSpokenIntro = useRef(false);

  // Initial prompt from Ori
  useEffect(() => {
    if (hasSpokenIntro.current) return;
    hasSpokenIntro.current = true;
    const intro = t('orbIntake.intro');
    setLastReply(intro);
    speak(intro);
    // eslint-disable-next-line
  }, []);

  const speak = useCallback((text) => {
    if (!text || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    const langMap = { de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
                      pt: 'pt-PT', tr: 'tr-TR', pl: 'pl-PL', uk: 'uk-UA', ar: 'ar-SA' };
    utt.lang = langMap[lang] || 'en-US';
    utt.rate = 1.0;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [lang]);

  const sendToClaude = useCallback(async (text) => {
    if (!text?.trim()) return;
    setThinking(true);
    setError('');
    try {
      const res = await axios.post(`${API}/orchestrator/claude/chat`, {
        session_id: sessionId,
        message: text,
        lang,
        known_fields: knownFields,
      });
      const { reply, extracted, complete: isDone } = res.data || {};
      const newKnown = { ...knownFields, ...(extracted || {}) };
      setKnownFields(newKnown);
      setLastReply(reply || '');
      speak(reply);
      if (isDone) {
        setComplete(true);
        await finalizeRegistration(newKnown);
      }
    } catch (e) {
      setError(e.response?.data?.detail || t('common.error'));
    }
    setThinking(false);
  }, [sessionId, lang, knownFields, speak, t]);

  const finalizeRegistration = async (allFields) => {
    try {
      const res = await axios.post(`${API}/auth/register-voice`, {
        extracted: allFields,
        lang,
      });
      if (res.data?.access_token) {
        setRegistration({
          magic_link: res.data.magic_link,
          email: allFields.email,
          name: allFields.firstName,
        });
        // After registration, Ori asks if user wants to see a test ride
        setTimeout(() => speak(t('orbIntake.askTestRide', { name: allFields.firstName })), 1600);
      }
    } catch (e) {
      const code = e.response?.data?.detail;
      const key = `auth.errors.${code}`;
      const tx = t(key);
      setError(tx !== key ? tx : (code || t('common.error')));
      speak(tx !== key ? tx : t('orbIntake.tryAgain'));
    }
  };

  // Demo match — static sample to showcase the MatchCard live
  const triggerDemoMatch = () => {
    const sampleName = lang === 'en' ? 'Klaus Müller' : 'Klaus Müller';
    setDemoMatch({
      id: 'demo-match-1',
      name: sampleName,
      rating: 4.9,
      rating_count: 67,
      verified: true,
      languages: ['DE', 'EN'],
      from_city: lang === 'en' ? 'Wedel' : 'Wedel',
      to_city: lang === 'en' ? 'Hamburg' : 'Hamburg',
      departure_time: new Date(Date.now() + 45 * 60 * 1000).toISOString(),
      vehicle_class: lang === 'en' ? 'Compact' : 'Kompaktklasse',
      price_per_km: 0.29,
      total_price: 8.70,
      match_score: 0.94,
    });
    speak(t('orbIntake.demoMatchSpeak'));
  };

  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setError(t('orbIntake.noSpeech'));
      return;
    }
    if (speaking) window.speechSynthesis.cancel();
    const rec = new SR();
    const langMap = { de: 'de-DE', en: 'en-US', fr: 'fr-FR', es: 'es-ES', it: 'it-IT',
                      pt: 'pt-PT', tr: 'tr-TR', pl: 'pl-PL', uk: 'uk-UA', ar: 'ar-SA' };
    rec.lang = langMap[lang] || 'en-US';
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => { setListening(true); setTranscript(''); };
    rec.onresult = (evt) => {
      let interim = '';
      let final = '';
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const tr = evt.results[i][0].transcript;
        if (evt.results[i].isFinal) final += tr;
        else interim += tr;
      }
      setTranscript(final || interim);
      if (final) {
        rec.stop();
        sendToClaude(final.trim());
      }
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        setError(e.error || t('common.error'));
      }
    };
    rec.onend = () => { setListening(false); };
    recognitionRef.current = rec;
    rec.start();
  }, [lang, speaking, sendToClaude, t]);

  const stopListening = () => {
    recognitionRef.current?.stop();
    setListening(false);
  };

  // Registration complete — show magic link CTA + demo offer
  if (registration) {
    return (
      <div style={styles.wrapper}>
        <button onClick={() => navigate('/')} style={styles.closeBtn} data-testid="orb-intake-close">
          <X size={22} />
        </button>
        <div style={styles.card} data-testid="orb-intake-success">
          <CheckCircle size={54} color="#22c55e" style={{ marginBottom: 10 }} />
          <h1 style={styles.successTitle}>{t('orbIntake.successTitle', { name: registration.name })}</h1>
          <p style={styles.successText}>
            {t('orbIntake.successText', { email: registration.email })}
          </p>
          {registration.magic_link && (
            <a
              href={registration.magic_link}
              data-testid="orb-intake-magic-link"
              style={styles.magicBtn}
            >
              <Sparkles size={18} /> {t('orbIntake.magicBtn')}
            </a>
          )}
          <div style={styles.demoSeparator}>
            <span style={{ fontSize: 12, color: '#64748b' }}>{t('orbIntake.or', 'oder')}</span>
          </div>
          <button
            onClick={triggerDemoMatch}
            data-testid="orb-intake-demo-btn"
            style={styles.demoBtn}
          >
            <Car size={18} /> {t('orbIntake.demoBtn')}
          </button>
          <p style={styles.devNote}>{t('orbIntake.devNote')}</p>
        </div>

        {/* Demo MatchCard appears below the success card */}
        {demoMatch && (
          <MatchCard
            match={demoMatch}
            onAccept={() => {
              speak(t('orbIntake.demoAccept'));
              setTimeout(() => setDemoMatch(null), 1800);
            }}
            onReject={() => setDemoMatch(null)}
            onViewProfile={null}
          />
        )}
      </div>
    );
  }

  return (
    <div style={styles.wrapper}>
      <button onClick={() => navigate('/')} style={styles.closeBtn} data-testid="orb-intake-close">
        <X size={22} />
      </button>

      {/* Pulsing Orb */}
      <div style={styles.orbContainer}>
        <div
          style={{
            ...styles.orb,
            ...(listening ? styles.orbListening : {}),
            ...(speaking ? styles.orbSpeaking : {}),
            ...(thinking ? styles.orbThinking : {}),
          }}
          data-testid="orb-intake-orb"
        />
        <div style={styles.orbRing} />
      </div>

      {/* Ori's last reply */}
      <div style={styles.replyBox} data-testid="orb-intake-reply">
        {thinking ? t('orbIntake.thinking') : lastReply || ' '}
      </div>

      {/* User's live transcript */}
      {transcript && (
        <div style={styles.transcript} data-testid="orb-intake-transcript">
          „{transcript}"
        </div>
      )}

      {/* Mic button */}
      <button
        onClick={listening ? stopListening : startListening}
        disabled={speaking || thinking}
        data-testid="orb-intake-mic"
        style={{
          ...styles.micBtn,
          ...(listening ? styles.micBtnActive : {}),
          opacity: (speaking || thinking) ? 0.5 : 1,
          cursor: (speaking || thinking) ? 'not-allowed' : 'pointer',
        }}
      >
        {listening ? <MicOff size={30} /> : <Mic size={30} />}
      </button>
      <div style={styles.hint}>
        {listening ? t('orbIntake.listening') : speaking ? t('orbIntake.speaking') : t('orbIntake.tapToSpeak')}
      </div>

      {error && <div style={styles.errorBox} data-testid="orb-intake-error">{error}</div>}

      {/* Known fields (small debug/transparency) */}
      {Object.keys(knownFields).length > 0 && (
        <div style={styles.knownBox} data-testid="orb-intake-known">
          {Object.entries(knownFields).map(([k, v]) => (
            <span key={k} style={styles.knownChip}>
              {k}: <b>{String(v)}</b>
            </span>
          ))}
        </div>
      )}

      <style>{`
        @keyframes orbPulse {
          0%, 100% { transform: scale(1); filter: brightness(1); }
          50% { transform: scale(1.06); filter: brightness(1.3); }
        }
        @keyframes orbRing {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.6); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  wrapper: {
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'radial-gradient(circle at 50% 40%, #1e1b4b 0%, #0f172a 60%, #000 100%)',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: 24, color: '#f8fafc',
  },
  closeBtn: {
    position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '50%',
    width: 42, height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#cbd5e1', cursor: 'pointer',
  },
  orbContainer: { position: 'relative', width: 220, height: 220, marginBottom: 24 },
  orb: {
    position: 'absolute', inset: 20,
    background: 'radial-gradient(circle at 35% 30%, #67e8f9 0%, #0ea5e9 50%, #1e1b4b 100%)',
    borderRadius: '50%',
    boxShadow: '0 0 80px rgba(0,212,255,0.4), inset 0 0 40px rgba(255,255,255,0.15)',
    transition: 'all 0.3s ease',
  },
  orbListening: {
    animation: 'orbPulse 1.2s ease-in-out infinite',
    boxShadow: '0 0 120px rgba(34,197,94,0.6), inset 0 0 40px rgba(255,255,255,0.2)',
    background: 'radial-gradient(circle at 35% 30%, #86efac 0%, #22c55e 50%, #065f46 100%)',
  },
  orbSpeaking: {
    animation: 'orbPulse 0.8s ease-in-out infinite',
    boxShadow: '0 0 120px rgba(167,139,250,0.6), inset 0 0 40px rgba(255,255,255,0.2)',
    background: 'radial-gradient(circle at 35% 30%, #c4b5fd 0%, #8b5cf6 50%, #3730a3 100%)',
  },
  orbThinking: { animation: 'orbPulse 2s ease-in-out infinite' },
  orbRing: {
    position: 'absolute', inset: 0, borderRadius: '50%',
    border: '2px solid rgba(0,212,255,0.3)',
    animation: 'orbRing 2s ease-out infinite', pointerEvents: 'none',
  },
  replyBox: {
    maxWidth: 460, textAlign: 'center', minHeight: 56, marginBottom: 14,
    fontSize: 16, lineHeight: 1.5, color: '#f1f5f9',
    padding: '10px 16px',
    background: 'rgba(15,23,42,0.6)', borderRadius: 14,
    border: '1px solid rgba(167,139,250,0.2)',
  },
  transcript: { fontSize: 13, color: '#94a3b8', fontStyle: 'italic', marginBottom: 10 },
  micBtn: {
    width: 76, height: 76, borderRadius: '50%',
    background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)', border: 'none',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 10px 30px rgba(0,212,255,0.4)',
    transition: 'all 0.2s ease',
  },
  micBtnActive: {
    background: 'linear-gradient(135deg,#ef4444,#dc2626)',
    boxShadow: '0 10px 30px rgba(239,68,68,0.5)',
  },
  hint: { marginTop: 12, fontSize: 12, color: '#94a3b8', letterSpacing: 0.4 },
  errorBox: {
    marginTop: 16, padding: '10px 14px', borderRadius: 10,
    background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
    color: '#fca5a5', fontSize: 13, maxWidth: 460, textAlign: 'center',
  },
  knownBox: {
    position: 'fixed', bottom: 20, left: 20, right: 20,
    display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center',
  },
  knownChip: {
    padding: '4px 10px', borderRadius: 999,
    background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
    color: '#86efac', fontSize: 11,
  },
  card: {
    maxWidth: 460, padding: 32, background: 'rgba(15,23,42,0.85)',
    border: '1px solid rgba(0,212,255,0.3)', borderRadius: 20, textAlign: 'center',
    boxShadow: '0 30px 80px rgba(0,0,0,0.5)',
  },
  successTitle: { fontSize: 24, fontWeight: 700, margin: '0 0 8px' },
  successText: { fontSize: 14, color: '#94a3b8', margin: '0 0 20px', lineHeight: 1.5 },
  magicBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '12px 24px', borderRadius: 12, textDecoration: 'none',
    background: 'linear-gradient(135deg,#00d4ff,#0ea5e9)',
    color: '#fff', fontWeight: 600, fontSize: 15,
    boxShadow: '0 10px 30px rgba(0,212,255,0.35)',
  },
  demoSeparator: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '18px 0 10px',
  },
  demoBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '12px 20px', borderRadius: 12, cursor: 'pointer',
    background: 'transparent',
    border: '1px solid rgba(167,139,250,0.4)',
    color: '#c4b5fd', fontWeight: 600, fontSize: 14,
  },
  devNote: {
    marginTop: 16, fontSize: 11, color: '#64748b',
    fontFamily: 'monospace', opacity: 0.7,
  },
};
